import { useCallback, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { RotateCcw, ZoomIn, ZoomOut } from 'lucide-react';

interface AvatarCropperProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageSrc: string;
  onCropComplete: (dataUrl: string) => void;
  saving?: boolean;
  /** Override the dialog heading (e.g. when cropping a team photo, not an avatar). */
  title?: string;
  /** Override the helper line under the heading. */
  description?: string;
  /** Override the confirm button label (defaults to "Save Avatar"). */
  saveLabel?: string;
}

const TARGET_SIZE = 256;
const FRAME_SIZE = 300;

interface ImageSize {
  width: number;
  height: number;
}

interface Offset {
  x: number;
  y: number;
}

function clampOffset(offset: Offset, displayWidth: number, displayHeight: number): Offset {
  return {
    x: Math.min(0, Math.max(FRAME_SIZE - displayWidth, offset.x)),
    y: Math.min(0, Math.max(FRAME_SIZE - displayHeight, offset.y)),
  };
}

function getDisplaySize(imageSize: ImageSize | null, zoom: number) {
  if (!imageSize) return { width: FRAME_SIZE, height: FRAME_SIZE, baseScale: 1 };
  const baseScale = FRAME_SIZE / Math.min(imageSize.width, imageSize.height);
  return {
    width: imageSize.width * baseScale * zoom,
    height: imageSize.height * baseScale * zoom,
    baseScale,
  };
}

function getCenteredOffset(displayWidth: number, displayHeight: number): Offset {
  return clampOffset(
    {
      x: (FRAME_SIZE - displayWidth) / 2,
      y: (FRAME_SIZE - displayHeight) / 2,
    },
    displayWidth,
    displayHeight,
  );
}

export function AvatarCropper({
  open,
  onOpenChange,
  imageSrc,
  onCropComplete,
  saving = false,
  title = 'Crop Your Avatar',
  description = 'Drag the photo behind the circle to choose any part of it. Zoom in or out, then save the circle crop.',
  saveLabel = 'Save Avatar',
}: AvatarCropperProps) {
  const [imageSize, setImageSize] = useState<ImageSize | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState<Offset>({ x: 0, y: 0 });
  const dragStartRef = useRef<{ pointerX: number; pointerY: number; offset: Offset } | null>(null);

  const { width: displayWidth, height: displayHeight } = getDisplaySize(imageSize, zoom);

  const resetCrop = useCallback(() => {
    const nextZoom = 1;
    const { width, height } = getDisplaySize(imageSize, nextZoom);
    setZoom(nextZoom);
    setOffset(getCenteredOffset(width, height));
  }, [imageSize]);

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const nextSize = { width: e.currentTarget.naturalWidth, height: e.currentTarget.naturalHeight };
    const { width, height } = getDisplaySize(nextSize, 1);
    setImageSize(nextSize);
    setZoom(1);
    setOffset(getCenteredOffset(width, height));
  }, []);

  const updateZoom = (values: number[]) => {
    const nextZoom = values[0];
    const previousCenter = {
      x: FRAME_SIZE / 2 - offset.x,
      y: FRAME_SIZE / 2 - offset.y,
    };
    const previousDisplay = getDisplaySize(imageSize, zoom);
    const nextDisplay = getDisplaySize(imageSize, nextZoom);
    const imageCenterRatio = {
      x: previousCenter.x / previousDisplay.width,
      y: previousCenter.y / previousDisplay.height,
    };
    const nextOffset = {
      x: FRAME_SIZE / 2 - imageCenterRatio.x * nextDisplay.width,
      y: FRAME_SIZE / 2 - imageCenterRatio.y * nextDisplay.height,
    };

    setZoom(nextZoom);
    setOffset(clampOffset(nextOffset, nextDisplay.width, nextDisplay.height));
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    dragStartRef.current = { pointerX: event.clientX, pointerY: event.clientY, offset };
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStartRef.current) return;
    const nextOffset = {
      x: dragStartRef.current.offset.x + event.clientX - dragStartRef.current.pointerX,
      y: dragStartRef.current.offset.y + event.clientY - dragStartRef.current.pointerY,
    };
    setOffset(clampOffset(nextOffset, displayWidth, displayHeight));
  };

  const handlePointerEnd = () => {
    dragStartRef.current = null;
  };

  const handleCropComplete = () => {
    if (!imageSize) return;

    const canvas = document.createElement('canvas');
    canvas.width = TARGET_SIZE;
    canvas.height = TARGET_SIZE;

    const ctx = canvas.getContext('2d');
    const image = new Image();
    if (!ctx) return;

    image.onload = () => {
      const sourceX = Math.max(0, (-offset.x / displayWidth) * imageSize.width);
      const sourceY = Math.max(0, (-offset.y / displayHeight) * imageSize.height);
      const sourceSize = Math.min(
        imageSize.width - sourceX,
        imageSize.height - sourceY,
        (FRAME_SIZE / displayWidth) * imageSize.width,
        (FRAME_SIZE / displayHeight) * imageSize.height,
      );

      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(image, sourceX, sourceY, sourceSize, sourceSize, 0, 0, TARGET_SIZE, TARGET_SIZE);
      onCropComplete(canvas.toDataURL('image/jpeg', 0.86));
    };

    image.src = imageSrc;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {description}
          </p>

          <div className="flex items-center gap-4 rounded-lg bg-muted p-3">
            <ZoomOut className="h-4 w-4 text-muted-foreground" />
            <Slider
              value={[zoom]}
              onValueChange={updateZoom}
              min={1}
              max={4}
              step={0.05}
              className="flex-1"
              aria-label="Avatar zoom"
            />
            <ZoomIn className="h-4 w-4 text-muted-foreground" />
            <Label className="min-w-[50px] text-sm text-muted-foreground">{Math.round(zoom * 100)}%</Label>
            <Button type="button" variant="ghost" size="icon" onClick={resetCrop} aria-label="Reset avatar crop">
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex justify-center rounded-lg bg-muted/50 p-4">
            <div
              className="relative cursor-grab overflow-hidden rounded-full border-2 border-primary/80 bg-background shadow-inner active:cursor-grabbing"
              style={{ width: FRAME_SIZE, height: FRAME_SIZE }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerEnd}
              onPointerCancel={handlePointerEnd}
              role="application"
              aria-label="Drag avatar photo to reposition crop"
            >
              <img
                src={imageSrc}
                alt="Avatar crop preview"
                onLoad={onImageLoad}
                draggable={false}
                className="absolute max-w-none select-none"
                style={{
                  width: displayWidth,
                  height: displayHeight,
                  transform: `translate(${offset.x}px, ${offset.y}px)`,
                  touchAction: 'none',
                }}
              />
              <div className="pointer-events-none absolute inset-0 rounded-full ring-2 ring-white/80 ring-inset" />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" onClick={handleCropComplete} disabled={saving || !imageSize}>
            {saving ? 'Saving...' : saveLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
